/**
 * Thin client for creator-program RPCs and Edge Functions.
 * Centralizes calls to the M1 backend so studio + admin pages stay tidy.
 */
import { supabase } from './supabaseStorage';
import type {
  AgentApprovalDecision,
  AgentVersionPayload,
  CreatorPublicProfile,
  PreflightReport,
} from '../types/creator';

interface RpcResult {
  ok?: boolean;
  error?: string;
  [k: string]: unknown;
}

async function callRpc<T extends RpcResult>(
  fn: string,
  args: Record<string, unknown>
): Promise<{ ok: true; data: T } | { ok: false; error: string }> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) return { ok: false, error: error.message };
  const row = (data ?? null) as T | null;
  if (!row) return { ok: false, error: `${fn}: empty response` };
  if (row.ok === false) return { ok: false, error: row.error || `${fn}: refused` };
  return { ok: true, data: row };
}

export async function superadminGrantCreatorRole(opts: {
  targetHyUserId: string;
  handle?: string | null;
}) {
  return callRpc<RpcResult & { creator_handle?: string | null }>('superadmin_grant_creator_role', {
    p_target_hy_user_id: opts.targetHyUserId,
    p_handle: opts.handle ?? null,
  });
}

export async function superadminRevokeCreatorRole(opts: { targetHyUserId: string }) {
  return callRpc<RpcResult>('superadmin_revoke_creator_role', {
    p_target_hy_user_id: opts.targetHyUserId,
  });
}

export async function creatorCreateTemplateDraft(opts: {
  initialPayload?: Partial<AgentVersionPayload>;
}) {
  return callRpc<RpcResult & { template_id?: string; version_id?: string }>(
    'creator_create_template_draft',
    {
      p_initial_payload: opts.initialPayload ?? {},
    }
  );
}

export async function creatorCreateNewDraft(opts: { templateId: string }) {
  return callRpc<RpcResult & { template_id?: string; version_id?: string }>(
    'creator_create_new_draft',
    { p_template_id: opts.templateId }
  );
}

export async function superadminDecideVersion(opts: {
  versionId: string;
  decision: AgentApprovalDecision;
  reason?: string | null;
}) {
  return callRpc<RpcResult & { template_id?: string; version_id?: string }>('superadmin_decide_version', {
    p_version_id: opts.versionId,
    p_decision: opts.decision,
    p_reason: opts.reason ?? null,
  });
}

/** Calls the `creator-submit-version` Edge Function. */
export async function creatorSubmitVersion(opts: { versionId: string }) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) return { ok: false as const, error: 'Not authenticated' };

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const res = await fetch(`${supabaseUrl}/functions/v1/creator-submit-version`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
    },
    body: JSON.stringify({ version_id: opts.versionId }),
  });
  const json = (await res.json().catch(() => null)) as
    | { ok?: boolean; error?: string; preflight?: PreflightReport; version_id?: string; template_id?: string }
    | null;

  if (!res.ok || !json?.ok) {
    return {
      ok: false as const,
      error: json?.error || `Submit failed (${res.status})`,
      preflight: json?.preflight ?? null,
    };
  }
  return {
    ok: true as const,
    version_id: json.version_id ?? null,
    template_id: json.template_id ?? null,
    preflight: json.preflight ?? null,
  };
}

/** Calls the `creator-notify-decision` Edge Function (best-effort). */
export async function creatorNotifyDecision(opts: {
  versionId: string;
  decision: AgentApprovalDecision;
  reason?: string | null;
}) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) return { ok: false as const, error: 'Not authenticated' };

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const res = await fetch(`${supabaseUrl}/functions/v1/creator-notify-decision`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
    },
    body: JSON.stringify({
      version_id: opts.versionId,
      decision: opts.decision,
      reason: opts.reason ?? null,
    }),
  });
  const json = (await res.json().catch(() => null)) as
    | { ok?: boolean; error?: string; delivered?: boolean; channels?: Record<string, unknown> }
    | null;

  if (!res.ok || !json?.ok) {
    return { ok: false as const, error: json?.error || `Notify failed (${res.status})` };
  }
  return { ok: true as const, delivered: json.delivered === true, channels: json.channels ?? null };
}

export type ReviewAiRecommendation = AgentApprovalDecision;

export interface ReviewAiKnowledgeBase {
  present: boolean;
  assessment: string;
  issues: string[];
}

export interface ReviewAiSuggestion {
  recommendation: ReviewAiRecommendation;
  confidence: 'low' | 'medium' | 'high';
  summary: string;
  note: string;
  concerns: string[];
  highlights: string[];
  knowledge_base_review: ReviewAiKnowledgeBase;
}

/** Calls the `creator-review-analyze` Edge Function. Superadmin only. */
export async function creatorReviewAnalyze(opts: { versionId: string }): Promise<
  | { ok: true; suggestion: ReviewAiSuggestion }
  | { ok: false; error: string }
> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) return { ok: false, error: 'Not authenticated' };

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const res = await fetch(`${supabaseUrl}/functions/v1/creator-review-analyze`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
    },
    body: JSON.stringify({ version_id: opts.versionId }),
  });
  const json = (await res.json().catch(() => null)) as
    | (Partial<ReviewAiSuggestion> & { ok?: boolean; error?: string })
    | null;

  if (!res.ok || !json?.ok) {
    return { ok: false, error: json?.error || `Review analysis failed (${res.status})` };
  }
  const rec = (json.recommendation as ReviewAiRecommendation) || 'changes_requested';
  const kbRaw = (json.knowledge_base_review ?? null) as Partial<ReviewAiKnowledgeBase> | null;
  return {
    ok: true,
    suggestion: {
      recommendation: rec,
      confidence: (json.confidence as ReviewAiSuggestion['confidence']) || 'medium',
      summary: json.summary || '',
      note: json.note || '',
      concerns: json.concerns ?? [],
      highlights: json.highlights ?? [],
      knowledge_base_review: {
        present: kbRaw?.present === true,
        assessment: kbRaw?.assessment || '',
        issues: kbRaw?.issues ?? [],
      },
    },
  };
}

/** Calls the public `get_creator_public_profile` RPC. */
export async function getCreatorPublicProfile(handle: string): Promise<CreatorPublicProfile | null> {
  const { data, error } = await supabase.rpc('get_creator_public_profile', {
    p_handle: handle,
  });
  if (error) {
    console.warn('[creatorApi] get_creator_public_profile:', error.message);
    return null;
  }
  if (!data || typeof data !== 'object') return null;
  return data as CreatorPublicProfile;
}

/** Best-effort landing visit logger; safe to fire-and-forget. */
export async function logCreatorLandingVisit(opts: {
  handle: string;
  templateSlug?: string | null;
  templateId?: string | null;
  referrer?: string | null;
}) {
  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    await fetch(`${supabaseUrl}/functions/v1/creator-landing-visit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
      },
      body: JSON.stringify({
        handle: opts.handle,
        template_slug: opts.templateSlug ?? null,
        template_id: opts.templateId ?? null,
        referrer: opts.referrer ?? null,
      }),
    });
  } catch {
    /* swallow — analytics is best-effort */
  }
}
